import {CustomerFormValues} from "../components/forms/UpdateCustomerForm";
import {
  addCustomer,
  assignSalespersonToCustomer,
  deleteCustomer,
  getCustomerById,
  getCustomers,
  getCustomersForSalesperson,
  getNonGBAPatientGroupsForCustomer,
  getNonGBAPatientsForAllCustomer,
  getUnassignedCustomers,
  updateCustomer
} from "../api/customers";
import {DbCustomer, NonGBAPatientResult} from "sharedlibs";
import {CustomerTableData} from "../models/CustomerTableData";
import SalesPersonService from "./SalesPersonService";

export default class CustomerService {

  static async getNonGBAForCustomer(customerId: string) {
    return await getNonGBAPatientGroupsForCustomer(customerId)
  }

  static async getNonGBAForAllCustomers(): Promise<NonGBAPatientResult[]> {
    return await getNonGBAPatientsForAllCustomer()
  }

  static async getAllCustomers(): Promise<DbCustomer[]> {
    const customers = await getCustomers()

    if (!Array.isArray(customers)) {
      throw new Error("Fetched Customers are not an array")
    }

    return customers
  }

  static async getCustomerById(id: string): Promise<DbCustomer> {
    return await getCustomerById(id)
  }

  static async addCustomer(formObject: CustomerFormValues): Promise<void> {
    const customer = this.createNewCustomer(formObject);
    const uniqueProperties = await this.checkUniqueProperties(customer);

    if (!uniqueProperties.isUnique) {
      throw new Error(uniqueProperties.notUniqueFields.join(", "));
    }

    await addCustomer(customer)
  }

  static async updateCustomer(oldCustomer: DbCustomer, formObject: CustomerFormValues) {
    const updatedCustomer = this.updateCustomerObject(oldCustomer, formObject)
    const uniqueProperties = await this.checkUniqueProperties(updatedCustomer)

    if (!(uniqueProperties.isUnique)) {
      const notUniqueFieldsText = uniqueProperties.notUniqueFields.join(", ");
      alert(`Some values which must be unique are not unique! Not unique fields: ${notUniqueFieldsText}. Customer will not be added!`);
      throw new Error("Customer values are not unique!")
    }

    console.log('Validation passed. Updating customer')
    await updateCustomer(updatedCustomer)
  }

  static async deleteCustomer(customerId: string, safe = true) {
    await deleteCustomer(customerId, safe)
  }

  static async getUnassignedCustomers(): Promise<DbCustomer[]> {
    return await getUnassignedCustomers()
  }

  static async getCustomersForSalesperson(salespersonId: string): Promise<DbCustomer[]> {
    return await getCustomersForSalesperson(salespersonId)
  }

  static async assignSalesperson(customerId: string, salespersonId: string | null): Promise<DbCustomer> {
    return await assignSalespersonToCustomer(customerId, salespersonId)
  }

  /**
   * The customer table rows, each with the last name of its assigned salesperson
   * (null when none is assigned) and whether that salesperson is deactivated. The
   * salesperson list is optional: if it fails to load, the customers still load and an
   * assigned salesperson is "" (unknown, not unassigned). A salesperson missing from a
   * loaded list (e.g. assigned between the two requests) is "" as well.
   */
  static async getCustomerTableData(): Promise<CustomerTableData[]> {
    const [customers, salesPersons] = await Promise.all([
      this.getAllCustomers(),
      SalesPersonService.getAllSalesPersons().catch((e) => {
        console.error(e)
        return undefined
      }),
    ])
    const salesPersonById = new Map(salesPersons?.map(sp => [sp.id, sp]))
    return customers.map(elem => {
      const salesPerson = elem.salespersonId ? salesPersonById.get(elem.salespersonId) : undefined
      return {
        ...this.toCustomerTableData(elem),
        salesperson: elem.salespersonId ? salesPerson?.lastName ?? "" : null,
        salespersonDisabled: salesPerson?.disabled ?? false,
      }
    })
  }

  private static toCustomerTableData(
    elem: DbCustomer
  ): Omit<CustomerTableData, 'salesperson' | 'salespersonDisabled'> {
    if (!elem.id) {
      throw new Error(`Data field: ${elem} does not contain .id parameter. \n` +
        "Without the identifier, the object cannot be updated or deleted")
    }
    return {
      id: elem.id,
      name: elem.name,
      doctorName: elem.doctorName,
      customerNumber: elem.customerNumber,
      postalCode: elem.address.postalCode,
      location: elem.address.city,
      street: elem.address.street,
      biotronikClientName: elem.biotronikClientName,
      biotronikCustomerNumber: elem.biotronikCustomerNumber,
      eTin: elem.eTin,
      bostonSAPID: elem.bostonSAPID,
    }
  }

  static mapCustomerObjectToFormObject(customerObject: DbCustomer): CustomerFormValues {
    return {
      name: customerObject.name,
      doctorName: customerObject.doctorName,
      customerNumber: customerObject.customerNumber,
      streetAndDwellingNumber: customerObject.address.street,
      zip: customerObject.address.postalCode,
      location: customerObject.address.city,
      biotronikClientName: customerObject.biotronikClientName,
      biotronikCustomerNumber: customerObject.biotronikCustomerNumber,
      eTin: customerObject.eTin,
      bostonSAPID: customerObject.bostonSAPID,
      abbottId: customerObject.abbottId,
      microportId: '', //TODO add this in backend,
      gbaGroupName: customerObject.gbaGroupName ?? "",
      nonGbaGroupName: customerObject.nonGbaGroupName ?? "",
      billingInHfGroups: customerObject.billingInHfGroups ?? false,
      hfBillingGroups: customerObject.hfBillingGroups ?? [],
    }
  }

  private static createNewCustomer(formObject: CustomerFormValues): DbCustomer {
    return {
      name: formObject.name,
      doctorName: formObject.doctorName,
      customerNumber: formObject.customerNumber,
      address: { // TODO replace with actual address
        street: formObject.streetAndDwellingNumber,
        postalCode: formObject.zip,
        city: formObject.location,
      },
      biotronikClientName: formObject.biotronikClientName,
      biotronikCustomerNumber: formObject.biotronikCustomerNumber,
      eTin: formObject.eTin,
      bostonSAPID: formObject.bostonSAPID,
      abbottId: formObject.abbottId,
      billingInHfGroups: formObject.billingInHfGroups,
      hfBillingGroups: formObject.hfBillingGroups ?? [],
    } // TODO add the missing form fields: abbottId, microportId, system
  }

  private static updateCustomerObject(customer: DbCustomer, formObject: CustomerFormValues): DbCustomer {
    if (customer.id === undefined) {
      throw new Error("To update a customer, the id property must exist")
    }

    return {
      id: customer.id,
      abbottId: formObject.abbottId,
      address: { // TODO replace with actual address  <-- (?)
        street: formObject.streetAndDwellingNumber,
        postalCode: formObject.zip,
        city: formObject.location,
      },
      biotronikClientName: formObject.biotronikClientName,
      biotronikCustomerNumber: formObject.biotronikCustomerNumber,
      bostonSAPID: formObject.bostonSAPID,
      customerNumber: formObject.customerNumber,
      doctorName: formObject.doctorName,
      eTin: formObject.eTin,
      name: formObject.name,
      gbaGroupName: formObject.gbaGroupName,
      nonGbaGroupName: formObject.nonGbaGroupName,
      billingInHfGroups: formObject.billingInHfGroups,
      hfBillingGroups: formObject.hfBillingGroups ?? [],
      // Salesperson is owned exclusively by PUT /customer/:id/salesperson;
      // the server ignores this field on the generic update, so we simply
      // don't send it here.
    }
  }

  /**
   * This function checks properties which must be unique in the Customer object.
   * Returns true if values are unique, false otherwise.
   * @param customer
   */
  static async checkUniqueProperties(customer: DbCustomer) {
    const result = await getCustomers()
    const notUnique = result.find((elem: any) => {
      return (elem.name === customer.name || elem.customerNumber === customer.customerNumber) && elem.id !== customer.id;
    });

    let notUniqueFields: string[] = []

    if (notUnique) {
      if (notUnique.name === customer.name) notUniqueFields.push("Name");
      if (notUnique.customerNumber === customer.customerNumber) notUniqueFields.push("Customer Number");
    }

    return {
      isUnique: !notUnique,
      notUniqueFields
    };
  }
}
